import React, {Component} from 'react';
import Header from '../Header';
import styles from './ChallengeWorkSpace.module.css';
import {withRouter} from 'react-router-dom';
import {rwbApi} from '../../../../shared/apis/api';
import DocumentIcon from '../svgs/DocumentIcon';
import {errorAlert} from '../../../../shared/utils/ErrorAlert';
import moment from 'moment';

export class ChallengeWorkSpace extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isDownloading: false,
      sortConfig: {
        key: null,
        direction: 'ascending',
      },
    };
    this.downloadData = this.getDownloadDataFromTitleName(
      this.props.location.state.titleName,
    );
  }

  handleCSVDataResponse = (baseDownloadName, blob, startDate, endDate) => {
    const link = document.createElement('a');
    // workspace_name-key
    let downloadName = baseDownloadName;
    if (startDate) downloadName += `-start_${startDate}`;
    if (endDate) downloadName += `-end_${endDate}`;
    downloadName += '.csv';
    link.download = downloadName;
    link.href = window.URL.createObjectURL(blob);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // wait just a tick before re-enabling the download button
    setTimeout(() => {
      this.setState({isDownloading: false});
    }, 500);
  };

  // challenges
  downloadWorkspaceLeaderboardCsv = (workspace_id, workspace_name, item) => {
    const challengeId = item?.challenge_id || null;
    const {startDate, endDate} = this.props.location.state;
    this.setState({isDownloading: true});
    rwbApi
      .getWorkspaceLeaderboardCsv(workspace_id, {
        startDate,
        endDate,
        challengeId,
      })
      .then((blob) => {
        let baseName = `${workspace_name}-leaderboard`;
        if (challengeId) baseName += `-challenge_${challengeId}`;
        this.handleCSVDataResponse(baseName, blob, startDate, endDate);
      })
      .catch((error) => {
        console.warn('error getting csv: ', error);
        errorAlert('Error downloading workspace leaderboard CSV');
        this.setState({isDownloading: false});
      });
  };
  // events
  downloadWorkspaceAttendeeCsv = (workspace_id, workspace_name, item) => {
    const eventId = item?.event_id || null;
    const {startDate, endDate} = this.props.location.state;
    this.setState({isDownloading: true});
    rwbApi
      .getWorkspaceAttendeeCsv(workspace_id, {startDate, endDate, eventId})
      .then((blob) => {
        let baseName = `${workspace_name}-attendees`;
        if (eventId) baseName += `-event_${eventId}`;
        this.handleCSVDataResponse(baseName, blob, startDate, endDate);
      })
      .catch((error) => {
        console.warn('error getting csv: ', error);
        errorAlert('Error downloading workspace attendee CSV');
        this.setState({isDownloading: false});
      });
  };
  // users
  downloadWorkspaceUserCsv = (workspace_id, workspace_name) => {
    const {startDate, endDate} = this.props.location.state;
    this.setState({isDownloading: true});
    rwbApi
      .getWorkspaceUserCsv(workspace_id, {startDate, endDate})
      .then((blob) => {
        this.handleCSVDataResponse(
          `${workspace_name}-user`,
          blob,
          startDate,
          endDate,
        );
      })
      .catch((error) => {
        console.warn('error getting csv: ', error);
        errorAlert('Error downloading workspace user CSV');
        this.setState({isDownloading: false});
      });
  };

  handleSort = (key) => {
    let direction = 'ascending';
    if (
      this.state.sortConfig.key === key &&
      this.state.sortConfig.direction === 'ascending'
    ) {
      direction = 'descending';
    }
    this.setState({sortConfig: {key, direction}});
  };

  getSortedData = (data) => {
    const {sortConfig} = this.state;
    if (!sortConfig.key) return data;

    const sortedData = [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle numbers
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'ascending'
          ? aValue - bValue
          : bValue - aValue;
      }

      // Handle strings (case-insensitive)
      const aString = String(aValue).toLowerCase();
      const bString = String(bValue).toLowerCase();

      if (aString < bString) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aString > bString) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });

    return sortedData;
  };

  getSortIndicator = (columnKey) => {
    const {sortConfig} = this.state;
    if (sortConfig.key !== columnKey) {
      return ' ↕'; // Both arrows when not sorted
    }
    return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
  };

  getTitles = (keyMapping) => {
    return keyMapping.map((key) => {
      return key
        .split('_')
        .map((word) => {
          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');
    });
  };

  renderHTMLTable = (data, keyMapping) => {
    if (!data || data.length === 0) {
      return <p className={styles.emptyState}>No data available</p>;
    }

    const originalKeys = keyMapping?.slice() || Object.keys(data[0] || {});
    // can adjust keys here if we need to
    let visibleKeys = originalKeys;
    if (this.downloadData.workspaceDataKey === 'users') {
      visibleKeys = visibleKeys.filter((key) => key !== 'download');
    }
    if (visibleKeys.length === 0) {
      return <p className={styles.emptyState}>No displayable columns</p>;
    }

    const titles = this.getTitles(visibleKeys);
    const sortedData = this.getSortedData(data);

    return (
      <div className={styles.tableContainer}>
        <table>
          <thead>
            <tr>
              {titles.map((title, index) => {
                const columnKey = visibleKeys[index];
                return (
                  <th
                    scope="col"
                    key={index}
                    onClick={() => this.handleSort(columnKey)}
                    style={{cursor: 'pointer', userSelect: 'none'}}
                    title={`Click to sort by ${title}`}>
                    {title}
                    {this.getSortIndicator(columnKey)}
                  </th>
                );
              })}
              {this.downloadData.workspaceDataKey !== 'users' && (
                <th>Download</th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, index) => (
              <tr key={index}>
                {visibleKeys.map((key, idx) => {
                  const value = item[key];
                  const formattedValue =
                    typeof value === 'string' && moment(value).isValid()
                      ? moment(value).toISOString().slice(0, -5)
                      : String(value);
                  return (
                    <td scope="row" key={idx}>
                      {formattedValue}
                    </td>
                  );
                })}
                {this.downloadData.workspaceDataKey !== 'users' && (
                  <td>
                    <div
                      className={
                        this.state.isDownloading
                          ? `${styles.csvButton} ${styles.csvDisabled}`
                          : styles.csvButton
                      }
                      disabled={this.state.isDownloading}
                      onClick={
                        !this.state.isDownloading
                          ? () =>
                              this.downloadData.apiCall(
                                this.props.location.state.workspaceData
                                  .workspace_id,
                                this.props.location.state.workspaceData
                                  .workspace_name,
                                item,
                              )
                          : undefined
                      }>
                      <DocumentIcon tintColor={'var(--white)'} />
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  getDownloadDataFromTitleName = (titleName) => {
    switch (titleName) {
      case 'Events':
        return {
          apiCall: this.downloadWorkspaceAttendeeCsv,
          workspaceDataKey: 'events',
        };
      case 'Challenges':
        return {
          apiCall: this.downloadWorkspaceLeaderboardCsv,
          workspaceDataKey: 'challenges',
        };
      case 'Users':
        return {
          apiCall: this.downloadWorkspaceUserCsv,
          workspaceDataKey: 'users',
        };
    }
  };

  render() {
    const {
      workspaceData,
      titleName,
      startDate,
      endDate,
    } = this.props.location.state;
    const {history} = this.props;
    const {isDownloading} = this.state;

    return (
      <div>
        <div className={[styles.headerContainer]}>
          <Header
            title={titleName}
            onBack={() => {
              history.goBack();
            }}
          />
        </div>

        <div className={[styles.contentContainer]}>
          <div className={[styles.challengeEventContainer]}>
            <>
              <div
                className={
                  isDownloading
                    ? `${styles.csvButton} ${styles.csvDisabled}`
                    : styles.csvButton
                }
                onClick={
                  !isDownloading
                    ? () =>
                        this.downloadData.apiCall(
                          workspaceData.workspace_id,
                          workspaceData.workspace_name,
                        )
                    : undefined
                }>
                <DocumentIcon tintColor={'var(--white)'} />
                {isDownloading ? 'Prepping CSV...' : 'Download CSV'}
              </div>

              {this.renderHTMLTable(
                workspaceData[this.downloadData.workspaceDataKey],
                Object.keys(
                  workspaceData[this.downloadData.workspaceDataKey][0] || {},
                ),
              )}
            </>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(ChallengeWorkSpace);
